import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {
  Col,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiInput,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UploadResponse,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import UploadImageModal from '~/components/modals/UploadImageModal';
import ViewImageModal from '~/components/modals/ViewImageModal';
import {PAGE_URLS} from '~/constants';
import {
  ID_TYPE_OPTIONS,
  PHOTO_ID_TYPE_VALUES,
  WEIGHT_LOSS_TWO_KEYS,
  WEIGHT_LOSS_TWO_QUESTIONNAIRES,
} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

interface Props {}

const MDQ2Questionnaire2Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder, collectedData} = mdq;

  const [isShowPhotoId, setIsShowPhotoId] = useState(false);
  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_TWO_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_TWO_KEYS.ADDITIONAL_DETAILS);

  const currentUser = collectedData?.[WEIGHT_LOSS_TWO_KEYS.ADDITIONAL_DETAILS]?.data;

  const rules = {
    idType: {presence: {allowEmpty: false}},
    drugAllergies: {presence: {allowEmpty: false}},
    idNumber: {
      presence: {message: `^ID Number can't be blank`},
      length: function () {
        if (inputs.idType === PHOTO_ID_TYPE_VALUES.NRIC)
          return {
            maximum: 9,
            message: '^ID Number is too long (maximum is 9 characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          };
        return false;
      },
    },
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      idType: currentUser?.idType || '',
      idNumber: currentUser?.idNumber || '',
      drugAllergies: currentUser?.drugAllergies || '',
      photoUID: currentUser?.photoUID || '',
      nricFileUid: currentUser?.nricFileUid || '',
      photoURL: currentUser?.photoURL || '',
    },
    {rules},
  );

  const onUploadSuccess = (files: UploadResponse[]) => {
    setInputs({photoUID: files?.[0]?.uid, nricFileUid: files?.[0]?.uid, photoURL: files?.[0]?.accessUrl});
  };

  const onClickSubmit = () => {
    if (validation.getErrors()) {
      return;
    }
    const answerData = {
      key: WEIGHT_LOSS_TWO_KEYS.ADDITIONAL_DETAILS,
      index: questionnaire?.index || '',
      data: {
        idType: inputs.idType,
        idNumber: inputs.idNumber,
        drugAllergies: inputs.drugAllergies,
        photoUID: inputs.photoUID,
        nricFileUid: inputs.nricFileUid,
        photoURL: inputs.photoURL,
      },
      order: answerOrder,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_TWO_KEYS.ADDITIONAL_DETAILS]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.HOW_IT_WORK}));
    history.push(PAGE_URLS.MDQ2_OBESITY_CHECK + window.location.search);
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.photoUID || !inputs.idType || !inputs.idNumber || !inputs.drugAllergies}
    >
      <ViewImageModal
        isShowing={isShowPhotoId}
        onDismissModal={() => setIsShowPhotoId(false)}
        imgUrl={inputs.photoURL}
        isCentered
      />
      <UploadImageModal
        isShowing={isShowUploadModal}
        onDismissModal={() => setIsShowUploadModal(false)}
        onSuccessUpload={onUploadSuccess}
      />
      <Col alignItems="center" textAlign="center" mt={20} mb={1} px={2}>
        <UiText fontSize="xl" fontWeight="700" mb={2}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" mb={2} maxW={80}>
          {questionnaire?.questions?.sub}
        </UiText>
        <UiCard mt={4} w="100%" borderRadius="1rem" boxShadow="0px 6px 25px 0px rgba(0, 0, 0, 0.10)" pt={4} pb={9}>
          <UiFormControl id="idType" isRequired isInvalid={!!validation?.errors?.idType}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              ID Type
            </UiFormLabel>
            <CustomDropdown
              onChange={value => setInputs({idType: value?.value})}
              options={ID_TYPE_OPTIONS}
              defaultItem={ID_TYPE_OPTIONS.find(i => i.value === inputs.idType)}
              selectedFontWeight="600"
            />
            {validation?.errors?.idType && <UiFormErrorMessage>{validation?.errors?.idType[0]}</UiFormErrorMessage>}
          </UiFormControl>
          <UiFormControl id="idNumber" isRequired isInvalid={!!validation?.errors?.idNumber}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              ID number
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.idNumber}
              maxLength={inputs.idType === PHOTO_ID_TYPE_VALUES.NRIC ? 9 : undefined}
              onChange={({target}) => setInputs({idNumber: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.idNumber && <UiFormErrorMessage>{validation?.errors?.idNumber[0]}</UiFormErrorMessage>}
          </UiFormControl>
          <UiFormControl id="drugAllergies" isRequired isInvalid={!!validation?.errors?.drugAllergies}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Drug allergies
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.drugAllergies}
              onChange={({target}) => setInputs({drugAllergies: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.drugAllergies && (
              <UiFormErrorMessage>{validation?.errors?.drugAllergies[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiCard>
        <UiSimpleGrid columns={2} mt={7} w="100%">
          <UiButton
            title="View Photo ID"
            variant="outline"
            onClick={() => setIsShowPhotoId(true)}
            borderColor="trueBlack"
            color="trueBlack"
            _hover={{}}
            disabled={!inputs.photoURL}
          />
          <UiButton
            ml="10px"
            title={inputs.photoUID ? 'Upload New ID' : 'Upload ID'}
            variant="outline"
            borderColor="trueBlack"
            color="trueBlack"
            _hover={{}}
            onClick={() => setIsShowUploadModal(true)}
          />
        </UiSimpleGrid>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQ2Questionnaire2Screen;
